/**
 * Typed accessors for the `/kit-templates` Backend_API routes.
 */

import { useMemo } from 'react';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';
import type {
  ActivateKitTemplateResponse,
  CreateKitTemplateBackgroundUploadRequest,
  CreateKitTemplateBackgroundUploadResponse,
  CreateKitTemplateRequest,
  CreateKitTemplateResponse,
  DeleteKitTemplateResponse,
  ListKitTemplatesResponse,
} from '../shared/types';

export type KitTemplateBackgroundUploadResult = { ok: true } | { ok: false; error: string };

export async function uploadKitTemplateBackgroundToS3(
  presigned: CreateKitTemplateBackgroundUploadResponse,
  file: File,
): Promise<KitTemplateBackgroundUploadResult> {
  const form = new FormData();
  for (const [k, v] of Object.entries(presigned.fields)) {
    form.append(k, v);
  }
  form.append('file', file);
  try {
    const response = await fetch(presigned.url, { method: 'POST', body: form });
    if (!response.ok) return { ok: false, error: `s3_upload_failed_${response.status}` };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export function useKitTemplatesApi() {
  const api = useApi();
  return useMemo(
    () => ({
      list: (): Promise<ApiResult<ListKitTemplatesResponse>> =>
        api.request<ListKitTemplatesResponse>('/kit-templates', { method: 'GET' }),
      create: (body: CreateKitTemplateRequest): Promise<ApiResult<CreateKitTemplateResponse>> =>
        api.request<CreateKitTemplateResponse>('/kit-templates', { method: 'POST', body }),
      activate: (templateId: string): Promise<ApiResult<ActivateKitTemplateResponse>> =>
        api.request<ActivateKitTemplateResponse>(
          `/kit-templates/${encodeURIComponent(templateId)}/activate`,
          { method: 'POST' },
        ),
      delete: (templateId: string): Promise<ApiResult<DeleteKitTemplateResponse>> =>
        api.request<DeleteKitTemplateResponse>(`/kit-templates/${encodeURIComponent(templateId)}`, {
          method: 'DELETE',
        }),
      uploadBackgroundUrl: (
        body: CreateKitTemplateBackgroundUploadRequest,
      ): Promise<ApiResult<CreateKitTemplateBackgroundUploadResponse>> =>
        api.request<CreateKitTemplateBackgroundUploadResponse>('/kit-templates/upload-url', {
          method: 'POST',
          body,
        }),
    }),
    [api],
  );
}
